import React from "react";
import { useAppDispatch, useAppSelector } from "../../../core/hooks/redux";
import {
  logout,
  setHasCompletedApplications,
  setSS,
  setToken,
} from "../../../store/core/account/accountSlice";
import {
  fetchGetExams,
  fetchGetNotificationsInfo,
} from "../../../store/modules/notifications/notificationsThunk";
import ResponseBody from "../../../http/models/ResponseBody";
import NotificationsInfo from "../../modules/notifications/interfaces/NotificationsInfo";
import { fetchGetDictionaries } from "../../../store/core/dictionaries/dictionariesThunk";
import { fetchGetAccount } from "../../../store/core/account/accountThunk";
import User, { ReloginCredentials } from "../../../core/interfaces/User";
import { loginRequest, logoutRequest } from "../../../http/requests/account.http";
import { getApplicationsRequest } from "../../../http/requests/applications/applications.http";
import setCompletedApplications from "../../modules/applications/utils/setCompletedApplications";
import degreeType from "../../../core/types/degreeType";
import ApplicationInfo from "../../../core/interfaces/applications/ApplicationInfo";
import ApplicationsLists from "../../../core/interfaces/applications/ApplicationsLists";
import { setReverseDomainMode } from "../../../core/constants/admissionDomainConfig";
import { resetProfileState } from "../../../store/modules/profile/profileForm/profileFormSlice";
import { resetAdditionalDataState } from "../../../store/modules/applications/additionalData/additionalDataSlice";
import { resetHighEducationState } from "../../../store/modules/applications/highEducation/highEducationSlice";
import { resetAdditionalEducationState } from "../../../store/modules/applications/additionalEducation/additionalEducationSlice";
import { resetAdditionalAeDataState } from "../../../store/modules/applications/additionalAEData/additionalAEDataSlice";
import { resetAdditionalPostgraduateDataState } from "../../../store/modules/applications/additionalPostgraduateData/additionalPostgraduateDataSlice";
import { resetPostgraduateEducationState } from "../../../store/modules/applications/postgraduateEducation/postgraduateEducationSlice";

const resetSessionModules = (dispatch: ReturnType<typeof useAppDispatch>): void => {
  dispatch(resetProfileState());
  dispatch(resetAdditionalDataState());
  dispatch(resetAdditionalAeDataState());
  dispatch(resetAdditionalPostgraduateDataState());
  dispatch(resetHighEducationState());
  dispatch(resetAdditionalEducationState());
  dispatch(resetPostgraduateEducationState());
};

const BackgroundProcesses: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const { accountState } = useAppSelector((state) => state.coreState);
  const didReloginRef = React.useRef(false);

  const getNotificationsHandler = (callbackFunc?: () => void): void => {
    dispatch(fetchGetNotificationsInfo()).then((res) => {
      const body = res.payload as ResponseBody<NotificationsInfo>;

      if (body.error || res.meta.requestStatus === "rejected") {
        return;
      }

      if (callbackFunc) {
        callbackFunc();
      }
    });
  };

  const getNotificationsPolling = (): void => {
    setTimeout(() => {
      getNotificationsHandler(getNotificationsPolling);
    }, 180000);
  };

  const performRelogin = async (credentials: ReloginCredentials): Promise<void> => {
    try {
      await logoutRequest();
    } catch {
      // серверная сессия могла уже быть невалидна — продолжаем локальный relogin
    }

    const loginRes = await loginRequest(credentials);

    if (loginRes.data.error) {
      setReverseDomainMode(false);
      dispatch(logout());
      resetSessionModules(dispatch);
      return;
    }

    setReverseDomainMode(false);
    dispatch(logout());
    resetSessionModules(dispatch);

    localStorage.setItem("token", loginRes.data.token);
    localStorage.setItem("ss", loginRes.data.payload.ss);
    dispatch(setSS(loginRes.data.payload.ss));
    dispatch(setToken(loginRes.data.token));
  };

  React.useEffect(() => {
    const lsToken = localStorage.getItem("token");

    if (lsToken) {
      dispatch(setToken(lsToken));
    }
  }, []);

  React.useEffect(() => {
    if (accountState.token) {
      dispatch(fetchGetDictionaries());
      dispatch(fetchGetAccount()).then(async (accountRes) => {
        const accountData = accountRes.payload as ResponseBody<User>;

        if (accountData?.error || !accountData?.payload) {
          return;
        }

        const { relogin } = accountData.payload;

        if (relogin?.email && relogin?.password && !didReloginRef.current) {
          didReloginRef.current = true;
          await performRelogin(relogin);
          return;
        }

        if (accountData.payload.hasProfile) {
          // SET HIGH EDUCATION
          getApplicationsRequest("high").then((notificationsRes) => {
            const applications = notificationsRes.data.payload as ApplicationsLists;
            const hasCompletedApplications = [
              ...applications.paid,
              ...applications.budget,
              ...applications.draft,
            ].some((application) => !!+application.completed);

            dispatch(
              setHasCompletedApplications({ degree: "high", value: hasCompletedApplications })
            );
          });

          // SET OTHER EDUCATION
          // (["additional", "postgraduate"] as degreeType[]).forEach((degree) => {
          (["additional"] as degreeType[]).forEach((degree) => {
            getApplicationsRequest(degree).then((notificationsRes) => {
              const applications = notificationsRes.data.payload;

              if (!Array.isArray(applications)) {
                return;
              }

              setCompletedApplications(applications as ApplicationInfo[], degree);
            });
          });

          getNotificationsHandler(getNotificationsPolling);
          dispatch(fetchGetExams());
        }
      });
    }
  }, [accountState.token]);

  return <>{children}</>;
};

export default BackgroundProcesses;
